var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState, useCallback } from "react";
import { Row, Col, AccordionSet, Accordion, ErrorModal, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { FieldRow } from "../../components/FieldRow";
import { AccordionSection } from "../../components/AccordionSection";
import { ExpandCollapseToggle } from "../../components/ExpandCollapseToggle";
import { InvestigationTimeline } from "../../components/investigation/InvestigationTimeline";
import { LibraryNoteRow } from "../../components/title/LibraryNoteRow";
import { ProvisionalBadge } from "../../components/common/ProvisionalBadge";
import { VariantTitlesList } from "../../components/common/VariantTitlesList";
import { NoteInputModal } from "../../components/investigation/NoteInputModal";
import { DuplicateTitlesBanner } from "../../components/title/merge/DuplicateTitlesBanner";
import { MergedRecordsSection } from "../../components/title/merge/MergedRecordsSection";
import { AgencyNotificationsSection } from "../../components/title/AgencyNotificationsSection";
import { useModal } from "../../utils/useModal";
import { provisionalKey } from "../../utils/provisionalTitleKeys";
import { startInvestigation, addInvestigationNote, fetchInvestigationNotes, resolveInvestigation, } from "../../api/titleService";
import { formatIsbn } from "../../utils/isbn";
import css from "./TitleDetail.module.css";
export var TitleDetail = function (_a) {
    var _b, _c, _d;
    var title = _a.title, investigationStatus = _a.investigationStatus, onInvestigationChanged = _a.onInvestigationChanged, _e = _a.startModalOpen, startModalOpen = _e === void 0 ? false : _e, onStartModalClose = _a.onStartModalClose, _f = _a.addNoteModalOpen, addNoteModalOpen = _f === void 0 ? false : _f, onAddNoteModalClose = _a.onAddNoteModalClose, _g = _a.resolveModalOpen, resolveModalOpen = _g === void 0 ? false : _g, onResolveModalClose = _a.onResolveModalClose, onOpenMerge = _a.onOpenMerge;
    var formatMessage = useIntl().formatMessage;
    var kyInstance = useOkapiKy();
    var queryClient = useQueryClient();
    var _h = useModal(), errorModalOpen = _h.isOpen, errorMessage = _h.message, showError = _h.openModal, hideError = _h.closeModal;
    var isInProgress = investigationStatus === "IN_PROGRESS";
    var _j = useState({
        general: true,
        investigation: true,
    }), accordionStatus = _j[0], setAccordionStatus = _j[1];
    var _k = useState(true), expandAll = _k[0], setExpandAll = _k[1];
    var notesQuery = useQuery(["investigationNotes", title.id], function () { return fetchInvestigationNotes(kyInstance, title.id); }, { enabled: isInProgress });
    var startMutation = useMutation(function (note) { return startInvestigation(kyInstance, title.id, note); }, {
        onSuccess: function () {
            onStartModalClose === null || onStartModalClose === void 0 ? void 0 : onStartModalClose();
            queryClient.invalidateQueries(["investigationNotes", title.id]);
            onInvestigationChanged === null || onInvestigationChanged === void 0 ? void 0 : onInvestigationChanged();
        },
        onError: function () {
            showError(formatMessage({
                id: "ui-br-legaldeposits.investigation.error.start",
            }));
        },
    });
    var addNoteMutation = useMutation(function (text) { return addInvestigationNote(kyInstance, title.id, text); }, {
        onSuccess: function () {
            onAddNoteModalClose === null || onAddNoteModalClose === void 0 ? void 0 : onAddNoteModalClose();
            queryClient.invalidateQueries(["investigationNotes", title.id]);
        },
        onError: function () {
            showError(formatMessage({
                id: "ui-br-legaldeposits.investigation.error.addNote",
            }));
        },
    });
    var resolveMutation = useMutation(function (note) {
        return resolveInvestigation(kyInstance, title.id, note || undefined);
    }, {
        onSuccess: function () {
            onResolveModalClose === null || onResolveModalClose === void 0 ? void 0 : onResolveModalClose();
            queryClient.invalidateQueries(["investigationNotes", title.id]);
            queryClient.invalidateQueries(["titlesForVerification"]);
            onInvestigationChanged === null || onInvestigationChanged === void 0 ? void 0 : onInvestigationChanged();
        },
        onError: function () {
            showError(formatMessage({
                id: "ui-br-legaldeposits.investigation.error.resolve",
            }));
        },
    });
    var toggleAccordion = useCallback(function (section) {
        setAccordionStatus(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[section] = !prev[section], _a)));
        });
    }, []);
    var toggleExpandCollapseAll = useCallback(function () {
        var newState = !expandAll;
        setAccordionStatus({ general: newState, investigation: newState });
        setExpandAll(newState);
    }, [expandAll]);
    var handleStartModalClose = useCallback(function () { return onStartModalClose === null || onStartModalClose === void 0 ? void 0 : onStartModalClose(); }, [onStartModalClose]);
    var handleStartModalSubmit = useCallback(function (text) { return startMutation.mutate(text); }, [startMutation]);
    var handleAddNoteModalClose = useCallback(function () { return onAddNoteModalClose === null || onAddNoteModalClose === void 0 ? void 0 : onAddNoteModalClose(); }, [onAddNoteModalClose]);
    var handleAddNoteModalSubmit = useCallback(function (text) { return addNoteMutation.mutate(text); }, [addNoteMutation]);
    var handleResolveModalClose = useCallback(function () { return onResolveModalClose === null || onResolveModalClose === void 0 ? void 0 : onResolveModalClose(); }, [onResolveModalClose]);
    var handleResolveModalSubmit = useCallback(function (text) { return resolveMutation.mutate(text); }, [resolveMutation]);
    return (React.createElement(React.Fragment, null,
        React.createElement("h1", null,
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.titles.detail.pane.title" }),
            title.provisional && React.createElement(ProvisionalBadge, null)),
        React.createElement(ErrorModal, { open: errorModalOpen, label: formatMessage({ id: "ui-br-legaldeposits.error.modal.title" }), content: errorMessage, onClose: hideError }),
        onOpenMerge && (React.createElement(DuplicateTitlesBanner, { titleId: title.id, onOpenMerge: onOpenMerge })),
        React.createElement(ExpandCollapseToggle, { expanded: expandAll, onToggle: toggleExpandCollapseAll }),
        React.createElement(AccordionSet, null,
            React.createElement(AccordionSection, { id: "general-info", labelId: "ui-br-legaldeposits.titles.accordion.general", open: accordionStatus.general, onToggle: function () { return toggleAccordion("general"); } },
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 12 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.titles.field.title", value: title.title }))),
                React.createElement(VariantTitlesList, { variants: title.variantTitles }),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.titles.field.author", value: title.author })),
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.titles.field.year", value: (_b = title.copyrightFrom) === null || _b === void 0 ? void 0 : _b.toString() }))),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: title.type === "PERIODICAL"
                                ? "ui-br-legaldeposits.titles.field.issn"
                                : "ui-br-legaldeposits.titles.field.isbn", value: title.type === "PERIODICAL"
                                ? title.issn
                                : formatIsbn(title.isbn) })),
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.titles.field.publisher", value: title.publisherNameToCheck || "-" }))),
                title.type !== "PERIODICAL" && (React.createElement(Row, null,
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.edition", value: title.editionStatement })),
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.binding", value: title.bindingText })))),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.titles.field.source", value: title.skc001 })),
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.titles.field.place", value: title.publisherPlaceToCheck }))),
                title.provisional && (React.createElement(Row, null,
                    React.createElement(Col, { xs: 12 },
                        React.createElement(FieldRow, { labelId: provisionalKey(title.type, "field.note"), value: title.provisionalNote || "-" })))),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 12 },
                        React.createElement(LibraryNoteRow, { note: (_c = title.libraryNote) !== null && _c !== void 0 ? _c : null })))),
            React.createElement(MergedRecordsSection, { titleId: title.id }),
            React.createElement(AgencyNotificationsSection, { titleId: title.id }),
            isInProgress && (React.createElement(Accordion, { label: React.createElement(React.Fragment, null,
                    formatMessage({
                        id: "ui-br-legaldeposits.investigation.accordion.history",
                    }),
                    React.createElement("span", { className: css.investigationBadge },
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.investigation.status.inProgress" }))), open: accordionStatus.investigation, onToggle: function () { return toggleAccordion("investigation"); } },
                React.createElement(InvestigationTimeline, { notes: (_d = notesQuery.data) !== null && _d !== void 0 ? _d : [], isLoading: notesQuery.isLoading })))),
        React.createElement(NoteInputModal, { open: startModalOpen, onClose: handleStartModalClose, onSubmit: handleStartModalSubmit, labelId: "ui-br-legaldeposits.investigation.modal.start.title", submitLabelId: "ui-br-legaldeposits.investigation.button.start", isLoading: startMutation.isLoading }),
        React.createElement(NoteInputModal, { open: addNoteModalOpen, onClose: handleAddNoteModalClose, onSubmit: handleAddNoteModalSubmit, labelId: "ui-br-legaldeposits.investigation.modal.addNote.title", submitLabelId: "ui-br-legaldeposits.investigation.button.addNote", isLoading: addNoteMutation.isLoading }),
        React.createElement(NoteInputModal, { open: resolveModalOpen, onClose: handleResolveModalClose, onSubmit: handleResolveModalSubmit, labelId: "ui-br-legaldeposits.investigation.modal.resolve.title", submitLabelId: "ui-br-legaldeposits.investigation.button.resolve", isLoading: resolveMutation.isLoading, noteRequired: false })));
};
